import React from 'react';

import ArticleIcon from '@mui/icons-material/Article';
import BuildIcon from '@mui/icons-material/Build';
import CodeIcon from '@mui/icons-material/Code';
import DescriptionIcon from '@mui/icons-material/Description';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import SchemaIcon from '@mui/icons-material/Schema';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import WorkflowArticleLayout from './components/WorkflowArticleLayout';
import WorkflowInfoBox from './components/WorkflowInfoBox';
import WorkflowLearningPoints from './components/WorkflowLearningPoints';
import WorkflowList from './components/WorkflowList';
import WorkflowPracticeBox from './components/WorkflowPracticeBox';
import WorkflowSection from './components/WorkflowSection';
import WorkflowSeriesComplete from './components/WorkflowSeriesComplete';
import WorkflowTable from './components/WorkflowTable';
import WorkflowTableOfContents from './components/WorkflowTableOfContents';
import WorkflowText from './components/WorkflowText';

const basicsSteps = [
  {
    label: 'Part1',
    title: 'Kiroとは: AIとの対話の基本原則',
    path: '/devdocs/workflow/kiro-basics-1',
  },
  {
    label: 'Part2',
    title: 'Steeringファイル: AIにルールを伝える方法',
    path: '/devdocs/workflow/kiro-basics-2',
  },
  {
    label: 'Part3',
    title: '機能と運用: 知っておくべきKiroの機能',
    path: '/devdocs/workflow/kiro-basics-3',
  },
  {
    label: 'Part4',
    title: 'ワークフロー全体像: 本プロジェクトの進め方',
    path: '/devdocs/workflow/kiro-basics-4',
    active: true,
  },
];

const WorkflowKiroBasics4: React.FC = () => {
  return (
    <WorkflowArticleLayout
      title="Kiro基本編 Part4 — ワークフロー全体像: 本プロジェクトの進め方"
      seriesName="Kiro基本編"
      tags={['Workflow', 'Architecture', 'Deliverables']}
      seriesSteps={basicsSteps}
      prevLink={{ label: 'Part3: 機能と運用', path: '/devdocs/workflow/kiro-basics-3' }}
      nextLink={{ label: '要件定義編 Part1 — RFPを作成する', path: '/devdocs/workflow/req-step1' }}
    >
      {/* この記事で学べること */}
      <WorkflowLearningPoints
        items={[
          '本プロジェクトのワークフロー全体像',
          'ウォーターフォールモデルとの成果物対応',
          'このシリーズの構成と各記事の位置づけ',
        ]}
      />

      <WorkflowTableOfContents
        items={[
          { id: 'section-1', label: 'ワークフロー全体像' },
          { id: 'section-2', label: '成果物の詳細一覧' },
          { id: 'section-3', label: 'このシリーズの構成' },
          { id: 'section-4', label: 'ベストプラクティスとアンチパターン' },
        ]}
      />

      {/* 1. ワークフロー全体像 */}
      <WorkflowSection title="ワークフロー全体像" id="section-1" number={1}>
        <WorkflowText>
          本プロジェクトでは、生成AIを活用した開発を以下のフローで行います。
        </WorkflowText>

        <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 2.5, overflow: 'auto' }}>
          {/* ヘッダー行 */}
          <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Chip
                label="AI開発フロー"
                size="small"
                color="primary"
                sx={{ height: 22, fontSize: '0.7rem', fontWeight: 700 }}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Chip
                label="一般的な開発フロー（ウォーターフォール）"
                size="small"
                sx={{
                  height: 22,
                  fontSize: '0.7rem',
                  fontWeight: 700,
                  bgcolor: 'action.hover',
                  color: 'text.secondary',
                }}
              />
            </Box>
          </Stack>

          <Stack spacing={0}>
            <FlowRow
              row={{
                ai: {
                  label: 'RFP作成 + AI対話で仕様策定',
                  icon: <NoteAddIcon sx={{ fontSize: 18 }} />,
                  color: '#1565c0',
                },
                wf: '企画・構想',
                outputs: ['*.rfp.md（要件草案 + 決定事項）'],
                wfOutputs: ['企画書', '要件概要', '業務フロー図', 'etc'],
              }}
            />
            <FlowDownArrows />
            <FlowRow
              row={{
                ai: {
                  label: 'proposal / business / usecases 出力',
                  icon: <DescriptionIcon sx={{ fontSize: 18 }} />,
                  color: '#2e7d32',
                },
                wf: '要件定義',
                outputs: ['*.proposal.md', '*.business.md', '*.usecases.md'],
                wfOutputs: ['要件定義書', '機能一覧', 'ユースケース図', 'etc'],
              }}
            />
            <FlowDownArrows />
            <FlowRow
              row={{
                ai: {
                  label: 'スキーマ定義 + API仕様書作成',
                  icon: <SchemaIcon sx={{ fontSize: 18 }} />,
                  color: '#6a1b9a',
                },
                wf: '基本設計',
                outputs: ['スキーマ定義.md', 'API仕様書.md', 'API一覧.md', '権限一覧.md'],
                wfOutputs: ['テーブル定義書', 'ER図', 'API仕様書', 'etc'],
              }}
            />
            <FlowDownArrows />
          </Stack>

          {/* api_project_rule ブロック */}
          <Stack direction="row" spacing={2} alignItems="stretch">
            <Box
              sx={{
                flex: '1 1 0',
                minWidth: 0,
                maxWidth: 'calc(50% - 8px)',
                border: 2,
                borderColor: '#e65100',
                borderRadius: 2,
                p: 2,
                position: 'relative',
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  top: -12,
                  left: 12,
                  px: 1,
                  py: 0.1,
                  bgcolor: 'background.paper',
                  borderRadius: 0.5,
                }}
              >
                <Typography variant="caption" fontWeight={700} sx={{ color: '#e65100' }}>
                  api_project_rule.md（Phase1→2→3）
                </Typography>
              </Box>
              <Stack spacing={0} sx={{ mt: 0.5 }}>
                {[
                  {
                    label: 'DD書作成（Phase1）',
                    icon: <BuildIcon sx={{ fontSize: 18 }} />,
                    outputs: ['diff.md', 'instructions.md', 'DD書.md'],
                  },
                  {
                    label: 'コード生成（Phase2）',
                    icon: <CodeIcon sx={{ fontSize: 18 }} />,
                    outputs: ['mk_instructions.md', 'Javaソースコード', 'Flyway SQL'],
                  },
                  {
                    label: 'テスト実行（Phase3）',
                    icon: <ArticleIcon sx={{ fontSize: 18 }} />,
                    outputs: ['test.md', '*.testcases.md', 'result_*.md'],
                  },
                ].map((step, idx, arr) => (
                  <React.Fragment key={idx}>
                    <Box
                      sx={{
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 2,
                        p: 1.5,
                        borderLeft: 3,
                        borderLeftColor: '#e65100',
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                        <Box sx={{ color: '#e65100' }}>{step.icon}</Box>
                        <Typography variant="body2" fontWeight={600}>
                          {step.label}
                        </Typography>
                      </Stack>
                      <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                        {step.outputs.map((o) => (
                          <Box
                            key={o}
                            sx={{
                              px: 0.75,
                              py: 0.1,
                              borderRadius: 0.5,
                              bgcolor: 'action.hover',
                              border: 1,
                              borderColor: 'divider',
                            }}
                          >
                            <Typography variant="caption" color="text.secondary">
                              {o}
                            </Typography>
                          </Box>
                        ))}
                      </Stack>
                    </Box>
                    {idx < arr.length - 1 && (
                      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <Box
                          sx={{
                            width: 0,
                            height: 0,
                            borderLeft: '6px solid transparent',
                            borderRight: '6px solid transparent',
                            borderTop: '8px solid',
                            borderTopColor: 'text.disabled',
                            my: 0.75,
                          }}
                        />
                      </Box>
                    )}
                  </React.Fragment>
                ))}
              </Stack>
            </Box>
            <Stack
              sx={{ flex: '1 1 0', minWidth: 0, maxWidth: 'calc(50% - 8px)' }}
              spacing={1}
              justifyContent="space-around"
            >
              {[
                { wf: '詳細設計', wfOutputs: ['詳細設計書', '処理フロー図', 'etc'] },
                { wf: '実装', wfOutputs: ['ソースコード', 'DDL', 'DML', 'etc'] },
                { wf: 'テスト', wfOutputs: ['テスト仕様書', 'テスト結果報告書', 'etc'] },
              ].map((item) => (
                <Box
                  key={item.wf}
                  sx={{
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 2,
                    p: 1.5,
                    bgcolor: (theme) =>
                      theme.palette.mode === 'dark'
                        ? 'rgba(69, 90, 100, 0.1)'
                        : 'rgba(69, 90, 100, 0.05)',
                  }}
                >
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    color="text.secondary"
                    sx={{ mb: 0.5 }}
                  >
                    {item.wf}
                  </Typography>
                  <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                    {item.wfOutputs.map((o) => (
                      <Box
                        key={o}
                        sx={{
                          px: 0.75,
                          py: 0.1,
                          borderRadius: 0.5,
                          bgcolor: 'action.hover',
                          border: 1,
                          borderColor: 'divider',
                        }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          {o}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              ))}
            </Stack>
          </Stack>
        </Box>

        <WorkflowInfoBox type="important">
          各フェーズは「成果物の作成 → ユーザー確認 →
          次へ進む」のサイクルで回る。AIは自律的に作業するが、各ステップで人間がレビュー・承認する。
        </WorkflowInfoBox>
      </WorkflowSection>

      {/* 2. 成果物の詳細一覧 */}
      <WorkflowSection title="成果物の詳細一覧" id="section-2" number={2}>
        <WorkflowTable
          headers={['ウォーターフォール工程', '本プロジェクトの成果物', '配置先']}
          rows={[
            ['要件定義', '*.rfp.md', '.kiro/requirements/{機能名}/'],
            ['要件定義', '*.proposal.md', '.kiro/requirements/{機能名}/'],
            ['要件定義', '*.business.md', '.kiro/requirements/usecases/'],
            ['要件定義', '*.usecases.md', '.kiro/requirements/usecases/'],
            ['基本設計', 'スキーマ定義 .md', 'docs/20_BD/10_スキーマ定義/'],
            ['基本設計', 'API仕様書 .md', 'docs/20_BD/30_API仕様書/'],
            ['基本設計', 'API一覧.md', 'docs/20_BD/20_API一覧/'],
            ['基本設計', '権限一覧.md', 'docs/20_BD/40_権限/'],
            ['詳細設計', 'DD書 .md', 'docs/30_DD/ 各サブディレクトリ'],
            ['実装', 'Java実装コード', 'src/main/java/'],
            ['実装', 'Flyway SQL', 'src/main/resources/db/migration/'],
            ['単体テスト', 'test.md', '.kiro/specs/10_API/{API_ID}/{ver}/'],
            ['結合テスト', '*.testcases.md', '.kiro/e2e_test/'],
            ['結合テスト', 'result_*.md', '.kiro/e2e_test/{テストケース名}/'],
          ]}
        />
      </WorkflowSection>

      {/* 3. このシリーズの構成 */}
      <WorkflowSection title="このシリーズの構成" id="section-3" number={3}>
        <Typography variant="body2" fontWeight={600}>
          Kiro基本編
        </Typography>
        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          {[
            {
              no: 'Part1',
              title: 'Kiro基本編 Part1',
              desc: 'Kiroとは、コンテキスト管理、#指定、クレジット節約',
            },
            {
              no: 'Part2',
              title: 'Kiro基本編 Part2',
              desc: '.kiro構造、Steeringの種類・仕組み・関連図',
            },
            {
              no: 'Part3',
              title: 'Kiro基本編 Part3',
              desc: 'Autopilot/Supervised、Specs、Hooks、幻覚対処',
            },
            {
              no: 'Part4',
              title: 'Kiro基本編 Part4（この記事）',
              desc: 'ワークフロー全体像、成果物一覧、シリーズ構成',
            },
          ].map((item) => (
            <Grid key={item.title} size={{ xs: 12, sm: 6 }}>
              <SeriesCard no={item.no} title={item.title} desc={item.desc} color="#546e7a" />
            </Grid>
          ))}
        </Grid>

        <Typography variant="body2" fontWeight={600}>
          要件定義編
        </Typography>
        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          {[
            {
              no: 'Part1',
              title: '要件定義編 Part1',
              desc: 'RFP作成 — 作りたい機能のイメージをまとめる',
            },
            {
              no: 'Part2',
              title: '要件定義編 Part2',
              desc: 'AI対話で仕様策定 — AIと対話しながら仕様を詰める',
            },
            {
              no: 'Part3',
              title: '要件定義編 Part3',
              desc: 'proposal/business出力 — 決定事項を構造化する',
            },
            {
              no: 'Part4',
              title: '要件定義編 Part4',
              desc: 'usecases出力 — システムユースケースを生成する',
            },
          ].map((item) => (
            <Grid key={item.title} size={{ xs: 12, sm: 6 }}>
              <SeriesCard no={item.no} title={item.title} desc={item.desc} color="#1565c0" />
            </Grid>
          ))}
        </Grid>

        <Typography variant="body2" fontWeight={600}>
          バックエンド開発編
        </Typography>
        <Grid container spacing={1.5}>
          {[
            { no: 'Part1', title: 'BE開発編 Part1', desc: 'スキーマ定義とAPI仕様書の作成' },
            { no: 'Part2', title: 'BE開発編 Part2', desc: 'api_project_rule ワークフローの全体像' },
            { no: 'Part3', title: 'BE開発編 Part3', desc: 'Phase1 — 設計書の作成' },
            { no: 'Part4', title: 'BE開発編 Part4', desc: 'Phase2 — コード生成' },
            { no: 'Part5', title: 'BE開発編 Part5', desc: 'Phase3 — テスト実行' },
            { no: 'Part6', title: 'BE開発編 Part6', desc: 'E2Eテスト' },
          ].map((item) => (
            <Grid key={item.title} size={{ xs: 12, sm: 6 }}>
              <SeriesCard no={item.no} title={item.title} desc={item.desc} color="#2e7d32" />
            </Grid>
          ))}
        </Grid>
      </WorkflowSection>

      {/* 4. ベストプラクティスとアンチパターン */}
      <WorkflowSection title="ベストプラクティスとアンチパターン" id="section-4" number={4}>
        <WorkflowPracticeBox
          type="best"
          items={[
            {
              practice: 'ワークフローの停止ポイントで必ず成果物を確認する',
              effect: 'AIの自動進行に任せると方針ズレが蓄積する。停止ポイントは品質ゲート',
            },
            {
              practice: '成果物は全てGit管理する',
              effect: '変更履歴を追跡でき、手戻り時に正確に差し戻せる',
            },
            {
              practice: '上流の成果物を修正したら下流も連動更新する',
              effect: 'proposal→API仕様書→DD書→コードの一貫性が品質の土台',
            },
            {
              practice: '1APIずつワークフローを回す（初回は特に）',
              effect: 'コンテキストが絞られてAIの出力精度が上がる',
            },
            {
              practice: '各フェーズの成果物テンプレートをSteeringに定義する',
              effect: 'AIが毎回同じ品質・形式で成果物を生成できる',
            },
            {
              practice: 'Rv指摘を蓄積してルール化する',
              effect: '過去の指摘をSteeringに追加することで同じミスの再発を防げる',
            },
          ]}
        />
        <WorkflowPracticeBox
          type="anti"
          items={[
            {
              practice: 'フェーズを飛ばして実行する（例: DD書なしでコード生成）',
              effect: '設計根拠のないコードが生成され品質が保証されない',
            },
            {
              practice: '停止ポイントを 0: で素通りする',
              effect: '方針ズレに気づかないまま後続フェーズに進み、大規模な手戻りが発生する',
            },
            {
              practice: 'コードだけ修正して設計書を放置する',
              effect: '次回開発時にAIが古い設計書を読んで誤ったコードを生成する',
            },
            {
              practice: '成果物をGit管理しない',
              effect: '変更履歴が追えず、手戻り時にどこまで戻せばよいか分からなくなる',
            },
            {
              practice: 'Rv指摘を記録せずに口頭で修正する',
              effect: '同じミスが繰り返されルール化の機会を逃す',
            },
            {
              practice: '関連性の薄い複数APIを同時にワークフローにかける',
              effect: 'AIのコンテキストが分散しDD書やコードの品質が低下する',
            },
          ]}
        />
      </WorkflowSection>

      {/* まとめ */}
      <WorkflowSection title="まとめ">
        <WorkflowTable
          headers={['Part', 'テーマ', '一言']}
          rows={[
            ['Part1', 'AIとの対話の基本原則', 'コンテキストを制するものが開発を制する'],
            ['Part2', 'Steeringファイル', 'ルールの質がAIの出力品質に直結する'],
            ['Part3', '機能と運用', '使う機能・使わない機能を判断する'],
            ['Part4', 'ワークフロー全体像', '成果物の流れを把握して次の記事に進む'],
          ]}
        />
        <WorkflowText sx={{ mt: 1 }}>
          次の記事からは、実際に要件定義フェーズでどのようにAIと協働するかを解説します。
        </WorkflowText>
      </WorkflowSection>

      <WorkflowSeriesComplete
        seriesName="Kiro基本編"
        message="Kiroの基本とワークフローの全体像を理解しました。次は実際に要件定義を進めましょう。"
        nextSeries={{ label: '要件定義編', path: '/devdocs/workflow/req-step1' }}
      />
    </WorkflowArticleLayout>
  );
};
// --- Sub components ---

const SeriesCard: React.FC<{ no: string; title: string; desc: string; color: string }> = ({
  no,
  title,
  desc,
  color,
}) => (
  <Box
    sx={{
      border: 1,
      borderColor: 'divider',
      borderRadius: 1.5,
      p: 1.5,
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
    }}
  >
    <Chip
      label={no}
      size="small"
      sx={{ height: 22, fontSize: '0.65rem', fontWeight: 700, bgcolor: `${color}20`, color }}
    />
    <Stack spacing={0}>
      <Typography variant="body2" fontWeight={600}>
        {title}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {desc}
      </Typography>
    </Stack>
  </Box>
);

type FlowRowData = {
  ai: { label: string; icon: React.ReactNode; color: string };
  wf: string;
  outputs: string[];
  wfOutputs: string[];
};

const FlowRow: React.FC<{ row: FlowRowData }> = ({ row }) => (
  <Stack direction="row" spacing={2} alignItems="stretch">
    <Box
      sx={{
        flex: '1 1 0',
        minWidth: 0,
        maxWidth: 'calc(50% - 8px)',
        border: 1,
        borderColor: 'divider',
        borderRadius: 2,
        p: 1.5,
        borderLeft: 3,
        borderLeftColor: row.ai.color,
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
        <Box sx={{ color: row.ai.color }}>{row.ai.icon}</Box>
        <Typography variant="body2" fontWeight={600}>
          {row.ai.label}
        </Typography>
      </Stack>
      <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
        {row.outputs.map((o) => (
          <Box
            key={o}
            sx={{
              px: 0.75,
              py: 0.1,
              borderRadius: 0.5,
              bgcolor: 'action.hover',
              border: 1,
              borderColor: 'divider',
            }}
          >
            <Typography variant="caption" color="text.secondary">
              {o}
            </Typography>
          </Box>
        ))}
      </Stack>
    </Box>
    <Box
      sx={{
        flex: '1 1 0',
        minWidth: 0,
        maxWidth: 'calc(50% - 8px)',
        border: 1,
        borderColor: 'divider',
        borderRadius: 2,
        p: 1.5,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        bgcolor: (theme) =>
          theme.palette.mode === 'dark' ? 'rgba(69, 90, 100, 0.1)' : 'rgba(69, 90, 100, 0.05)',
      }}
    >
      <Typography variant="body2" fontWeight={600} color="text.secondary" sx={{ mb: 0.5 }}>
        {row.wf}
      </Typography>
      <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
        {row.wfOutputs.map((o) => (
          <Box
            key={o}
            sx={{
              px: 0.75,
              py: 0.1,
              borderRadius: 0.5,
              bgcolor: 'action.hover',
              border: 1,
              borderColor: 'divider',
            }}
          >
            <Typography variant="caption" color="text.secondary">
              {o}
            </Typography>
          </Box>
        ))}
      </Stack>
    </Box>
  </Stack>
);

const FlowDownArrows: React.FC = () => (
  <Stack direction="row" spacing={2}>
    <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
      <Box
        sx={{
          width: 0,
          height: 0,
          borderLeft: '6px solid transparent',
          borderRight: '6px solid transparent',
          borderTop: '8px solid',
          borderTopColor: 'text.disabled',
          my: 0.75,
        }}
      />
    </Box>
    <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
      <Box
        sx={{
          width: 0,
          height: 0,
          borderLeft: '6px solid transparent',
          borderRight: '6px solid transparent',
          borderTop: '8px solid',
          borderTopColor: 'text.disabled',
          my: 0.75,
        }}
      />
    </Box>
  </Stack>
);

export default WorkflowKiroBasics4;
